import { takeLatest, put, call, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
  addAndUpdateContactsAction,
  getCompanyDataAction,
  getCompanyEditSeasonAction,
  getCompanyUnpublishedSeasonAction,
  updateCompanyInfoAction,
  updateCompanyVenueAction,
} from './actions';
// eslint-disable-next-line import/named
import { getCompany, getProductions, create, updateOne, deleteOne } from '../../utils/API';
import { enqueueSnackbarAction } from '../App/actions';
import { COMPANIES, COMPANY_CONTACTS, VENUES } from '../../constants/endpointConstants';
import { performancesAddedByIdentifiers, PRODUCTION_ADDED_IDENTIFIERS_IDS } from '../../constants/consts';

export function* fetchCompanyData({ payload }) {
  try {
    const response = yield call(getCompany, payload.companyId);
    yield put(getCompanyDataAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getCompanyDataAction.failure(err.message));
  } finally {
    yield put(getCompanyDataAction.fulfill());
  }
}

export function* fetchCompanyEditSeason({ payload }) {
  try {
    if (payload.companyId) {
      const productionsInSeason = yield call(
        getProductions,
        {
          sort: payload.sort || '-year',
          ...(payload?.query || {}),
          ...(payload.seasonId ? { season_id: payload.seasonId } : {}),
          approved_by: 'admin,company',
          company_id: payload.companyId,
          as_edit: true,
        },
        payload?.pagination || {},
      );
      yield put(getCompanyEditSeasonAction.success(get(productionsInSeason, 'data')));
    } else {
      yield put(getCompanyEditSeasonAction.success({ data: [] }));
    }
  } catch (err) {
    yield put(getCompanyEditSeasonAction.failure(err.message));
  } finally {
    yield put(getCompanyEditSeasonAction.fulfill());
  }
}

export function* fetchCompanyUnpublishedProductions({ payload }) {
  try {
    const productionsInSeason = yield call(
      getProductions,
      {
        company_id: payload.companyId,
        published: false,
        sort: '-updated_at',
        as_edit: true,
        added_by: performancesAddedByIdentifiers.COMPANY, // remove after api is ready for ownership
        for_entity: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY,
        for_entity_id: payload.companyId,
        ...(payload?.query || {}),
      },
      payload.pagination || {},
    );
    yield put(getCompanyUnpublishedSeasonAction.success(get(productionsInSeason, 'data')));
  } catch (err) {
    yield put(getCompanyUnpublishedSeasonAction.failure(err.message));
  } finally {
    yield put(getCompanyUnpublishedSeasonAction.fulfill());
  }
}

export function* addAndUpdateContacts({ payload }) {
  try {
    const newContacts = get(payload, 'newContacts', []);
    const updatedContacts = get(payload, 'updatedContacts', []);
    const deletedContacts = get(payload, 'deletedContacts', []);
    const endpoint = COMPANY_CONTACTS(payload?.companyId);
    const callback = get(payload, 'callback');
    yield all([
      ...(newContacts?.length > 0 ? [call(create, endpoint, { data: newContacts })] : []),
      ...(updatedContacts?.length > 0
        ? [call(updateOne, endpoint, updatedContacts[0]?.id, { data: updatedContacts })]
        : []),
      ...deletedContacts.map(deletedContactId => call(deleteOne, endpoint, deletedContactId)),
    ]);

    if (callback && typeof callback === 'function') {
      payload.callback(updatedContacts?.length + newContacts?.length + deletedContacts?.length);
    }
    yield put(
      enqueueSnackbarAction({
        message: `Success updating contacts`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating contacts: ${err?.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateCompanyVenue({ payload }) {
  const callback = get(payload, 'callback');
  const venues = get(payload, 'venues', []);
  try {
    yield all(venues?.map(v => call(updateOne, VENUES, v?.id, v)));
    if (callback && typeof callback === 'function') {
      payload.callback();
    }
    yield put(
      enqueueSnackbarAction({
        message: `Success updating venue`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating company info: ${err?.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateCompanyInfo({ payload }) {
  const callback = get(payload, 'callback');
  const companyId = get(payload, 'companyId');
  try {
    const result = yield all([
      call(updateOne, COMPANIES, companyId, payload?.companyInfo),
      call(addAndUpdateContacts, {
        payload: {
          companyId,
          newContacts: payload?.newContacts || [],
          updatedContacts: payload?.updatedContacts || [],
          deletedContacts: payload?.deletedContacts || [],
        },
      }),
    ]);

    if (callback && typeof callback === 'function') {
      payload.callback(result[0]?.data);
    }
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating company info: ${err?.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export default function* companySaga() {
  yield takeLatest(getCompanyDataAction.TRIGGER, fetchCompanyData);
  // TODO redo when published/unpublished productions will be totally ready
  yield takeLatest(getCompanyEditSeasonAction.TRIGGER, fetchCompanyEditSeason);
  yield takeLatest(getCompanyUnpublishedSeasonAction.TRIGGER, fetchCompanyUnpublishedProductions);
  yield takeLatest(addAndUpdateContactsAction.TRIGGER, addAndUpdateContacts);
  yield takeLatest(updateCompanyInfoAction.TRIGGER, updateCompanyInfo);
  yield takeLatest(updateCompanyVenueAction.TRIGGER, updateCompanyVenue);
}
